import { useSnapshot } from "valtio";
import { useEffect, useRef, useState } from "react";
import router, { useRouter } from "next/router";
import jwt_decode, { type JwtPayload } from "jwt-decode";

import store, { updateAccessToken } from "common/middleware/store";
import { mutateLoggedInUser } from "common/middleware/hooks";

import { getAuthRedirectUrl } from "common/utils/utils.auth";

export const useAuth = ({
  isAdminPortal,
  isCheckoutPortal,
  redirectToAuth,
}: {
  isAdminPortal?: boolean,
  isCheckoutPortal?: boolean,
  redirectToAuth?: boolean,
}) => {
  const { isSigningOut, accessToken } = useSnapshot(store);
  const { query, isReady } = useRouter();

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const appliedTokenFromQueryRef = useRef(false);

  useEffect(() => {
    if (!isReady || isSigningOut) {
      return;
    }

    if (query.token) {
      appliedTokenFromQueryRef.current = true;

      const tokenExpirationInSeconds = jwt_decode<JwtPayload>(query.token as string)?.exp;
      const tokenExpirationDateTime = tokenExpirationInSeconds ? new Date(tokenExpirationInSeconds * 1000) : undefined;

      if (query.refreshToken) {
        updateAccessToken({
          token: query.token as string,
          refreshToken: query.refreshToken as string,
          tokenExpirationDateTime,
        });
      } else {
        updateAccessToken({
          token: query.token as string,
          tokenExpirationDateTime,
        });
      }

      if (isAdminPortal) {
        mutateLoggedInUser();

        if (query.builderId) {
          store.builderId = query.builderId as string;
          delete query.builderId;
        }
      }

      delete query.token;
      delete query.refreshToken;
      router.replace({ query }, undefined, { shallow: true });
      return;
    }

    if (accessToken.token) {
      appliedTokenFromQueryRef.current = false;
      setIsAuthenticated(true);
      return;
    }

    if (redirectToAuth && !appliedTokenFromQueryRef.current) {
      window.location.href = isCheckoutPortal ? `${process.env.NEXT_PUBLIC_AUTH_URL}/login` : getAuthRedirectUrl();
    }
  }, [
    isReady,
    query,
    isSigningOut,
    accessToken.token,
  ]);

  return isAuthenticated;
}
